import MobxWrap from '@/components/MobxWrap';
import styles from './index.module.less';
import { useEffect, useMemo, useRef, useState } from 'react';
import { querySelector } from '@/utils';
import cls from 'classnames';
import { armsErrorSend } from '@/utils/libs/armsErrorSend';
import { ThirdPartyTracker } from '@/utils/Tracker';
import { PrimeCommodityMap } from '@/utils/configs/prime.config';
import { usePrimeBtnContext } from '../../usePrimeBtnContext';

// 对接id
const sdk = {
  prod: {
    dataSitId: 'ev7de9q63c',
    dataWidgetId: 'w-Vj4ZuFbkSh3fLBZdQZJEs2',
  },
  test: {
    dataSitId: 'u94ehcled0',
    dataWidgetId: 'w-HvCiz7iol48vpJpJ5BFJe7',
  },
};

type TCommodityId = keyof typeof PrimeCommodityMap;

type TPrimeButtonProps = {
  style: object;
  /** stikyBar | 相机购物清单 | 非相机页面上TopInfo的购物按钮 */
  from: 'stickyBar' | 'shoppingList' | 'productPage';
  /** 按钮显示回调 */
  onVisible?: (visible: boolean) => void;
};

/**
 * Prime购买按钮
 * @param style
 * @param from 'stickyBar' | 'shoppingList' | 'productPage'
 * @param onVisible 按钮显示回调
 */
const PrimeButton = MobxWrap<TPrimeButtonProps, ['product', 'area', 'app', 'abtest']>(
  ['product', 'area', 'app', 'abtest'],
  (props) => {
    const {
      from,
      style,
      onVisible,
      product: {
        productDetail: { categoryKey },
        curSelectCommodityId,
        productCarts,
        curSelectCommodity: { isOutOfStock, count },
      },
      area: { LANG },
      app: { isProd, urlFull },
      abtest: { isInVWOabtNotShowPrime },
    } = props;
    const [showDivider, setShowDivider] = useState(false);
    const primeBtnWrapRef = useRef<HTMLDivElement>(null);
    const primeBtnFrameRef = useRef<HTMLButtonElement | null>(null);
    const productSubmitBtnWrapRef = useRef<HTMLButtonElement | null>(null);

    const { isPrimeBtnVisible, setIsPrimeBtnVisible } = usePrimeBtnContext();

    const abtVisible = !isInVWOabtNotShowPrime && isPrimeBtnVisible;

    // 当前的套餐对应到的 prime仓库ID，顺序为-care-care_plus-extend
    const curPrimeSkuId = useMemo(() => {
      const productCartId = productCarts.reduce(
        (primeId, commodity, index) => (primeId += `${index === 0 ? '' : '+'}${commodity.id}`),
        ''
      ) as unknown as TCommodityId;
      return PrimeCommodityMap[productCartId as TCommodityId] ?? '';
    }, [curSelectCommodityId, productCarts]);

    // 当前的prime映射ID变更，主动更新prime cart组件状态（根据sku id按钮会自动显示隐藏，触发监听）
    useEffect(() => {
      try {
        if (window.bwp?.updateWidget && primeBtnFrameRef.current) {
          window.bwp?.updateWidget?.(`${curPrimeSkuId}`, count);
        }
      } catch (e) {
        setIsPrimeBtnVisible(false);
        armsErrorSend(e as Error, { filename: 'prime window.bwp?.updateWidget' });
      }
    }, [curPrimeSkuId]);

    // 按钮样式变动回调
    useEffect(() => {
      onVisible?.(abtVisible);
      setShowDivider(abtVisible);

      if (abtVisible) {
        ThirdPartyTracker.primeTracker({
          action: from === 'stickyBar' ? 'expose_bar_button' : 'expose_add_to_cart_button',
          label: `${curSelectCommodityId}`,
          value: from,
        });
      }
    }, [abtVisible]);

    // 产品页的宽度需要和上面的按钮对齐,缺货状态按钮长度会变
    const handlePrimeBtnWidth = () => {
      if (from === 'productPage' && primeBtnWrapRef.current) {
        if (!productSubmitBtnWrapRef.current) {
          querySelector("#product_submit_btn[data-from='page']").then((dom) => {
            productSubmitBtnWrapRef.current = dom as HTMLButtonElement;
            primeBtnWrapRef.current!.style.width = `${dom.clientWidth}px`;
          });
        } else {
          primeBtnWrapRef.current.style.width = `${productSubmitBtnWrapRef.current.clientWidth}px`;
        }
      }
    };

    // 页面上监听DOM节点属性查看按钮是否出现，如果没有出现则不展示OR按钮 / 移动端样式回退等
    const watchButtonVisibleChange = () => {
      let observer: MutationObserver | undefined;
      try {
        querySelector('#bwpFrame').then((targetNode) => {
          primeBtnFrameRef.current = targetNode as HTMLButtonElement;
          const callback = function (mutationsList: MutationRecord[]) {
            for (const mutation of mutationsList) {
              if (mutation.type === 'attributes') {
                const visible =
                  mutation.target && (mutation.target as HTMLElement).className.includes('bwp-widget-loaded');
                if (visible) handlePrimeBtnWidth();
                setIsPrimeBtnVisible(visible);
              }
            }
          };

          observer = new MutationObserver(callback);
          observer.observe(targetNode, { attributes: true, attributeFilter: ['class'] });
        });
      } catch (e) {
        // 如果报错了直接onVisible为false，不展示Prime，避免样式出问题。
        armsErrorSend(e as Error, { filename: 'prime addEventListener' });
        setIsPrimeBtnVisible(false);
      }

      return observer;
    };

    // 对按钮样式处理
    useEffect(() => {
      // 初始化处理宽度
      handlePrimeBtnWidth();

      // 页面上监听DOM节点属性查看按钮是否出现，如果没有出现则不展示OR按钮 / 移动端样式回退等
      const observer = watchButtonVisibleChange();

      return () => {
        observer?.disconnect();
      };
    }, []);

    // 点击埋点
    const onClick = () => {
      ThirdPartyTracker.primeTracker({
        action: from === 'stickyBar' ? 'click_bar_button' : 'click_add_to_cart_button',
        label: `${curSelectCommodityId}`,
        value: from,
      });
    };

    // 如果是虚拟服务直接return不展示按钮
    if (categoryKey === 'CF_CLIMB_SERVICE') return null;

    return (
      <div
        className={cls(styles.primeButtonWrap, !abtVisible && styles.hidden, isOutOfStock && styles.isOutOfStock)}
        ref={primeBtnWrapRef}
        style={style}
        data-from={from}
        onClick={onClick}
      >
        {from !== 'stickyBar' && showDivider && <div className={styles.divider}>{LANG['common.label.or']}</div>}
        <div className={styles.primeBtn}>
          <div
            id='amzn-buy-now'
            className={styles.primeBtnInner}
            data-site-id={sdk[isProd || urlFull.includes('prime_prod') ? 'prod' : 'test'].dataSitId}
            data-widget-id={sdk[isProd || urlFull.includes('prime_prod') ? 'prod' : 'test'].dataWidgetId}
            data-sku={curPrimeSkuId}
          />
        </div>
      </div>
    );
  }
);

export default PrimeButton;
